// ── Supabase Realtime messages ────────────────────────────────────────────
// Uses Supabase Realtime (postgres_changes) for live updates

import { supabase, db } from './supabase';

// ── Get or create conversation ─────────────────────────────────────────────
// mode: 'romantic' (from match) | 'friendly' (from BeeConnect)
export async function getOrCreateConversation(uid1, uid2) {
  const [user_a, user_b] = [uid1, uid2].sort();
  const { data: existing } = await db.conversations()
    .select('id')
    .eq('user_a', user_a)
    .eq('user_b', user_b)
    .maybeSingle();
  if (existing) return existing.id;

  const { data: created, error } = await db.conversations()
    .insert({ user_a, user_b }).select('id').single();
  if (error) throw new Error(error.message);
  return created.id;
}

// ── Send message ──────────────────────────────────────────────────────────
// `whisper`: when TRUE, the row gets is_whisper=TRUE; the BEFORE INSERT trigger
// (migration 037) sets expires_at = NOW() + 24h. Whispers are filtered from the
// UI once expired but rows are kept for moderation.
export async function sendMessage(convId, senderId, text, { whisper = false } = {}) {
  if (!text?.trim()) return { error: 'Empty message' };
  const payload = { conversation_id: convId, sender_id: senderId, text: text.trim() };
  if (whisper) payload.is_whisper = true;
  const { error: msgErr } = await db.messages().insert(payload);
  if (msgErr) return { error: msgErr.message };

  // Update conversation metadata
  await db.conversations().update({
    last_message: text.trim().slice(0, 100),
    last_message_at: new Date().toISOString(),
  }).eq('id', convId);

  return { error: null };
}

// ── Subscribe to messages (Realtime) ─────────────────────────────────────
export function subscribeToMessages(convId, callback) {
  // Initial load
  db.messages()
    .select('*')
    .eq('conversation_id', convId)
    .order('created_at', { ascending: true })
    .limit(200)
    .then(({ data }) => callback(data || []));

  // Realtime subscription — unique name to avoid "already subscribed" errors
  const channelName = `messages:${convId}:${Date.now()}`;
  const channel = supabase
    ?.channel(channelName)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `conversation_id=eq.${convId}`,
    }, (payload) => {
      callback(prev => {
        const existing = (prev || []);
        // Drop any optimistic row with same sender+text+approximate time, then append real row
        const filtered = existing.filter(m =>
          !(m.id?.toString().startsWith('opt-') &&
            m.sender_id === payload.new.sender_id &&
            m.text === payload.new.text)
        );
        // Avoid true duplicates (Realtime can fire twice)
        if (filtered.some(m => m.id === payload.new.id)) return filtered;
        return [...filtered, payload.new];
      });
    })
    .subscribe();

  return () => {
    channel?.unsubscribe();
    supabase?.removeChannel(channel);
  };
}

// ── Flatten partner profile (user_core + nested user_profiles) ────────────
function flattenPartner(raw) {
  if (!raw) return null;
  const { user_profiles, ...rest } = raw;
  return { ...rest, photos: user_profiles?.[0]?.photos || [] };
}

// ── Fetch conversations + partner profiles (two-step, no complex join) ───
async function fetchConvs(uid) {
  const { data: convRows, error } = await db.conversations()
    ?.select('id, user_a, user_b, mode, last_message, last_message_at, unread_a, unread_b')
    .or(`user_a.eq.${uid},user_b.eq.${uid}`)
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .limit(50) ?? {};

  if (error) { console.error('[conversations fetch]', error); return []; }
  if (!convRows?.length) return [];

  // Collect all partner IDs then fetch their profiles from the users view
  const partnerIds = [...new Set(convRows.map(c => c.user_a === uid ? c.user_b : c.user_a))];
  const { data: profiles, error: pErr } = await supabase
    ?.from('users')
    .select('id, name, age, photos, job, mood, last_active, plan') ?? {};
  if (pErr) console.error('[conversations profiles]', pErr);

  const byId = Object.fromEntries((profiles || []).filter(p => partnerIds.includes(p.id)).map(p => [p.id, p]));

  return convRows.map(c => {
    const partnerId = c.user_a === uid ? c.user_b : c.user_a;
    return {
      ...c,
      partner: byId[partnerId] || { id: partnerId },
      unread:  c.user_a === uid ? c.unread_a : c.unread_b,
    };
  });
}

// ── Subscribe to conversations list ──────────────────────────────────────
export function subscribeToConversations(uid, callback) {
  const fetchAndNotify = () => fetchConvs(uid).then(callback);

  fetchAndNotify();

  // Two channels: Supabase realtime doesn't support OR filters.
  const ts = Date.now();
  const chA = supabase
    ?.channel(`conversations_a:${uid}:${ts}`)
    .on('postgres_changes', {
      event: '*', schema: 'public', table: 'conversations',
      filter: `user_a=eq.${uid}`,
    }, fetchAndNotify)
    .subscribe();

  const chB = supabase
    ?.channel(`conversations_b:${uid}:${ts}`)
    .on('postgres_changes', {
      event: '*', schema: 'public', table: 'conversations',
      filter: `user_b=eq.${uid}`,
    }, fetchAndNotify)
    .subscribe();

  return () => {
    chA?.unsubscribe(); supabase?.removeChannel(chA);
    chB?.unsubscribe(); supabase?.removeChannel(chB);
  };
}

// ── Mark conversation read ────────────────────────────────────────────────
export async function markRead(convId, uid, isUserA) {
  const field = isUserA ? 'unread_a' : 'unread_b';
  await db.conversations().update({ [field]: 0 }).eq('id', convId);
}

// ── Mark messages as read ─────────────────────────────────────────────────
export async function markConversationRead(convId, userId) {
  if (!convId || !userId) return;
  // Update unread count on the conversation
  const { data: conv } = await db.conversations()
    ?.select('user_a, user_b, unread_a, unread_b')
    .eq('id', convId).single() || {};
  if (!conv) return;
  const field = conv.user_a === userId ? 'unread_a' : 'unread_b';
  await db.conversations()?.update({ [field]: 0 }).eq('id', convId);
}

// ── Load conversation history ──────────────────────────────────────────────
export async function loadMessages(convId, limit = 50) {
  const { data } = await db.messages()
    ?.select('*')
    .eq('conversation_id', convId)
    .order('created_at', { ascending: false })
    .limit(limit) || {};
  return (data || []).reverse();
}

// ── Get all conversations with partner profiles ───────────────────────────
export async function loadConversations(userId) {
  return fetchConvs(userId);
}
